import { useState } from "react";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../mock";

const CONTACT_INFO = [
  { icon: MapPin, label: "Head Office Address", value: CONTACT.address, href: null },
  { icon: Mail, label: "Direct Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Phone, label: "Telephone Contact", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete required fields", description: "Name, email and message are required.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const encode = (data) =>
      Object.keys(data)
        .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
        .join("&");
    try {
      // Netlify Forms submission (captured automatically when deployed on Netlify)
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
    } catch (err) {
      // Fallback: persist locally so no submission is lost
      const stored = JSON.parse(localStorage.getItem("gbb_inquiries") || "[]");
      stored.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("gbb_inquiries", JSON.stringify(stored));
    }
    setLoading(false);
    toast({ title: "Message received", description: "Thank you \u2014 our leadership team will respond shortly." });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32">
      <div className="absolute top-0 left-0 h-[380px] w-[380px] rounded-full bg-teal-500/8 blur-[130px]" />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div className="reveal">
            <span className="text-xs font-semibold tracking-[0.22em] text-teal-300 uppercase">
              {CONTACT.eyebrow}
            </span>
            <h2 className="font-display font-bold text-white text-3xl lg:text-4xl mt-4 leading-tight tracking-tight">
              {CONTACT.title}
            </h2>
            <p className="mt-6 text-slate-300/85 leading-relaxed">{CONTACT.intro}</p>

            <div className="mt-10 space-y-4">
              {CONTACT_INFO.map((item, i) => {
                const Icon = item.icon;
                const content = (
                  <div className="group flex items-center gap-4 rounded-xl border border-teal-500/10 bg-white/[0.02] p-4 transition-all hover:border-teal-400/30 hover:bg-white/[0.04]">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-300 transition-colors group-hover:bg-teal-500/20">
                      <Icon size={20} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{item.label}</p>
                      <p className="text-white font-medium mt-0.5">{item.value}</p>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a key={i} href={item.href} className="block">{content}</a>
                ) : (
                  <div key={i}>{content}</div>
                );
              })}
            </div>
          </div>

          <div className="reveal">
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="rounded-2xl border border-teal-500/12 bg-gradient-to-b from-white/[0.04] to-transparent p-6 sm:p-8"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="bg-[#04100f]/60 border-teal-500/15 text-white placeholder:text-slate-500 focus-visible:ring-teal-400/40 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com"
                    className="bg-[#04100f]/60 border-teal-500/15 text-white placeholder:text-slate-500 focus-visible:ring-teal-400/40 h-11"
                  />
                </div>
              </div>

              <div className="space-y-2 mt-5">
                <Label className="text-slate-300">Subject Area</Label>
                <input type="hidden" name="subject" value={form.subject} />
                <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                  <SelectTrigger className="bg-[#04100f]/60 border-teal-500/15 text-white h-11 focus:ring-teal-400/40">
                    <SelectValue placeholder="Select a subject area" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#071815] border-teal-500/20 text-white">
                    {CONTACT.subjects.map((s) => (
                      <SelectItem key={s} value={s} className="focus:bg-teal-500/15 focus:text-white">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 mt-5">
                <Label htmlFor="message" className="text-slate-300">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your engagement..."
                  rows={5}
                  className="bg-[#04100f]/60 border-teal-500/15 text-white placeholder:text-slate-500 focus-visible:ring-teal-400/40 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="group mt-7 w-full h-12 bg-gradient-to-r from-teal-400 to-emerald-500 hover:from-teal-300 hover:to-emerald-400 text-[#04100f] font-semibold rounded-lg text-base shadow-lg shadow-teal-500/20"
              >
                {loading ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</>
                ) : (
                  <>Send Message <Send size={17} className="ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}